import type { AttachmentOption } from '@src/types.ts/distribution-channel';

const formatCountAndNoun = (count: number, singular: string, plural: string) =>
  count === 1 ? `1 ${singular}` : `Up to ${count} ${plural}`;

const formatDurationSeconds = (seconds: number): string => {
  if (seconds >= 3600) {
    const hours = Math.round(seconds / 3600);
    return `≤${hours} hr`;
  }
  const minutes = Math.round(seconds / 60);
  if (minutes >= 1) return `≤${minutes} min`;
  return `≤${seconds}s`;
};

const formatImagePhrase = (
  attachment: Extract<AttachmentOption, { type: 'image' }>,
): string => {
  const noun = attachment.role === 'thumbnail' ? 'thumbnail' : 'photo';
  const pluralNoun = attachment.role === 'thumbnail' ? 'thumbnails' : 'photos';
  const count = formatCountAndNoun(attachment.maxCount, noun, pluralNoun);
  return attachment.maxSizeMb ? `${count} (≤${attachment.maxSizeMb}MB)` : count;
};

const formatVideoPhrase = (
  attachment: Extract<AttachmentOption, { type: 'video' }>,
): string => {
  const count = formatCountAndNoun(attachment.maxCount, 'video', 'videos');
  return attachment.maxSeconds
    ? `${count} (${formatDurationSeconds(attachment.maxSeconds)})`
    : count;
};

const formatDocumentPhrase = (
  attachment: Extract<AttachmentOption, { type: 'document' }>,
): string => {
  const count = formatCountAndNoun(attachment.maxCount, 'PDF', 'PDFs');
  return attachment.maxPages
    ? `${count} (≤${attachment.maxPages} pages)`
    : count;
};

const formatLinkSharePhrase = (
  attachment: Extract<AttachmentOption, { type: 'link_share' }>,
): string => formatCountAndNoun(attachment.maxCount, 'link', 'links');

const formatPollPhrase = (
  attachment: Extract<AttachmentOption, { type: 'poll' }>,
): string => formatCountAndNoun(attachment.maxCount, 'poll', 'polls');

export const formatTextLimitPhrase = (textMaxChars: number): string =>
  `≤${textMaxChars.toLocaleString()} chars`;

export const formatAttachmentPhrase = (
  attachment: AttachmentOption,
): string => {
  switch (attachment.type) {
    case 'image':
      return formatImagePhrase(attachment);
    case 'video':
      return formatVideoPhrase(attachment);
    case 'document':
      return formatDocumentPhrase(attachment);
    case 'link_share':
      return formatLinkSharePhrase(attachment);
    case 'poll':
      return formatPollPhrase(attachment);
  }
};
